import React from 'react';
import { useStateTracking } from '@tldraw/state-react';
import { useEditor, useValue } from 'tldraw';
import { isShapePendingUnmount } from '../utils/pendingShapeUnmount';

/**
 * Render the real shape util inside a reactive tracking scope (same idea as
 * tldraw's InnerShape). Needed so arrows re-draw when bound targets move —
 * their geometry comes from getArrowInfo, not from the arrow record itself.
 *
 * Always invoke util.component/indicator: those methods call hooks. Returning
 * null here when the store record is gone skips those hooks and throws
 * "Rendered fewer hooks than expected". If the record is already deleted,
 * reuse the last React prop so hook count stays stable until this fiber
 * unmounts.
 */
function BaseShapeContent({ util, baseUtil, shape, renderKey }) {
  return useStateTracking(
    `safe-shape-content:${renderKey}:${shape.id}`,
    () => {
      const latest = util.editor.store.unsafeGetWithoutCapture(shape.id) || shape;
      if (renderKey === 'component') {
        return baseUtil.prototype.component.call(util, latest);
      }
      if (renderKey === 'indicator') {
        return baseUtil.prototype.indicator.call(util, latest);
      }
      return baseUtil.prototype.backgroundComponent.call(util, latest);
    },
    [util, baseUtil, shape, renderKey]
  );
}

/**
 * tldraw's InnerShape can render while a shape record is already deleted.
 * Shape utils call hooks inside component/indicator. Delegating to a child
 * component that unmounts when the shape is gone keeps the parent's hook count
 * stable and avoids crashes in utils like ArrowShapeUtil / TextShapeUtil.
 *
 * Existence is gated on getShape (and pending unmount); the actual util render
 * uses useStateTracking so binding-dependent shapes follow moving targets.
 */
function SafeShapeLayer({ shapeId, util, baseUtil, renderKey }) {
  const editor = useEditor();
  const shape = useValue(
    `safe-shape-record-${shapeId ?? 'none'}-${renderKey}`,
    () => {
      if (!shapeId || isShapePendingUnmount(shapeId)) return null;
      return editor.getShape(shapeId) || null;
    },
    [editor, shapeId]
  );

  if (!shape) return null;

  return (
    <BaseShapeContent
      util={util}
      baseUtil={baseUtil}
      shape={shape}
      renderKey={renderKey}
    />
  );
}

export function createSafeShapeUtil(BaseUtil) {
  class SafeShapeUtil extends BaseUtil {
    component(shape) {
      return (
        <SafeShapeLayer
          shapeId={shape?.id}
          util={this}
          baseUtil={BaseUtil}
          renderKey="component"
        />
      );
    }

    indicator(shape) {
      return (
        <SafeShapeLayer
          shapeId={shape?.id}
          util={this}
          baseUtil={BaseUtil}
          renderKey="indicator"
        />
      );
    }
  }

  SafeShapeUtil.type = BaseUtil.type;
  SafeShapeUtil.props = BaseUtil.props;
  SafeShapeUtil.migrations = BaseUtil.migrations;

  if (typeof BaseUtil.prototype.backgroundComponent === 'function') {
    SafeShapeUtil.prototype.backgroundComponent = function backgroundComponent(shape) {
      return (
        <SafeShapeLayer
          shapeId={shape?.id}
          util={this}
          baseUtil={BaseUtil}
          renderKey="background"
        />
      );
    };
  }

  return SafeShapeUtil;
}
